import { useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { Link } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import { currencyFormatLong } from "components/app_layout/Typography";
import BidPipelineEmptyState from "components/bid_module/empty_states/BidPipelineEmptyState";
import { StageDropdownFilter } from "components/form_components/dropdown_list_selects/StageDropdownFilter";
import LeadPipelineEmptyState from "components/leads/empty_states/LeadPipelineEmptyState";
import { getDefaultLeadStageIds } from "components/leads/leads_table/utils";
import { processTransitions } from "components/shared/stage/utils";
import EmptyState from "components/ui/empty_state/EmptyState";
import Tabs, { TabItem } from "components/ui/tabs/Tabs";
import TooltipComponent, { TooltipGroup } from "components/ui/tooltip/Tooltip";
import { useDebouncedValue } from "lib/debounce";
import { useBidWorkflow } from "lib/hooks/api/bid/bids/useBidWorkflow";
import { useLeadWorkflow } from "lib/hooks/api/leads/useLeadWorkflow";
import { StandardViewName } from "lib/hooks/api/notices/views/useStandardViewTarget";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useLocalStorage } from "lib/hooks/useLocalStorage";
import { useMinimumLoadingTime } from "lib/hooks/useMinimumLoadingTime";
import { EventNames } from "lib/tracking";
import { pluralise } from "lib/utils";
import { generateBidsSearchUrl } from "../../bids/utils";
import { TeamDropdownFilter } from "../../form_components/dropdown_list_selects/TeamDropdownFilter";
import { UserDropdownFilter } from "../../form_components/dropdown_list_selects/UserDropdownFilter";
import { generateLeadsSearchUrl } from "../../leads/utils";
import {
  convertFiltersToStageFilterRequest,
  getDefaultBidPipelineFilters,
  getDefaultLeadPipelineFilters,
  PipelineFilters,
} from "../pipelineFilters";
import { Box, WidgetTitle } from "../util";
import { PipelineStage } from "./PipelineStage";
import { PipelineTab, StageItem } from "./types";

const TAB_ITEMS: TabItem[] = [
  {
    key: "leads",
    label: "Leads",
    icon: "target04",
    children: null,
  },
  {
    key: "bids",
    label: "Bids",
    icon: "feather",
    children: null,
  },
];

const EntityType = {
  leads: "Lead",
  bids: "Bid",
} as const;

type BidTransition = NonNullable<ReturnType<typeof useBidWorkflow>["data"]>["transitions"][number];
type LeadTransition = NonNullable<
  ReturnType<typeof useLeadWorkflow>["data"]
>["transitions"][number];

function TabbedPipeline() {
  const currentUser = useAssertCurrentUser();
  const currentUserTeamId = currentUser.team.id;
  const defaultLeadFilters = useMemo(
    () => getDefaultLeadPipelineFilters(currentUser),
    [currentUser],
  );
  const defaultBidFilters = useMemo(() => getDefaultBidPipelineFilters(), []);

  const [selectedTab, setSelectedTab] = useLocalStorage<PipelineTab>(
    `homepagePipelineTab_${currentUserTeamId}`,
    "leads",
  );
  const [leadFilters, setLeadFilters] = useLocalStorage<PipelineFilters>(
    `homepagePipelineLeadFilters_${currentUserTeamId}`,
    defaultLeadFilters,
  );
  const [bidFilters, setBidFilters] = useLocalStorage<PipelineFilters>(
    `homepagePipelineBidFilters_${currentUserTeamId}`,
    defaultBidFilters,
  );

  const filters = selectedTab === "leads" ? leadFilters : bidFilters;
  const setFilters = selectedTab === "leads" ? setLeadFilters : setBidFilters;
  const [debouncedFilters] = useDebouncedValue(filters, 500);

  const stageFilterRequest = convertFiltersToStageFilterRequest(debouncedFilters);

  const { data: leadWorkflow, isFetching: isFetchingLeads } = useLeadWorkflow(
    { withCounts: true, withStageValueSum: true, stageFilterRequest },
    { enabled: selectedTab === "leads", keepPreviousData: true },
  );
  const { data: bidWorkflow, isFetching: isFetchingBids } = useBidWorkflow(
    { withCounts: true, withStageValueSum: true, stageFilterRequest },
    { enabled: selectedTab === "bids", keepPreviousData: true },
  );

  // Materialize default lead stage IDs on first visit
  useEffect(() => {
    if (leadWorkflow && leadFilters.stageIds === undefined) {
      setLeadFilters((f) => ({
        ...f,
        stageIds: getDefaultLeadStageIds(leadWorkflow.transitions),
      }));
    }
  }, [leadWorkflow, leadFilters.stageIds, setLeadFilters]);

  // Apply minimum delay to both initial load and subsequent fetches
  const isLoadingLeads = useMinimumLoadingTime(isFetchingLeads, 200);
  const isLoadingBids = useMinimumLoadingTime(isFetchingBids, 200);

  const leadTransitions = leadWorkflow?.transitions ?? [];
  const bidTransitions = bidWorkflow?.transitions ?? [];
  const leadStageIds = leadFilters.stageIds ?? [];
  const filteredLeadTransitions = leadTransitions.filter(
    (t) => !leadStageIds.length || leadStageIds.includes(t.stage.id),
  );
  const isLeadPipelineEmpty =
    !isLoadingLeads &&
    leadTransitions.length > 0 &&
    leadTransitions.every((t) => (t.stage.leadCount ?? 0) === 0);

  const { totalCount: totalLeadsCount, totalValue: totalLeadsValue } = computeValueAndCount(
    filteredLeadTransitions,
    (t) => t.stage.leadCount ?? 0,
    (t) => t.stage.leadValueSum ?? 0,
  );

  const bidStageIds = bidFilters.stageIds ?? [];
  const filteredBidTransitions = bidTransitions.filter(
    (t) => !bidStageIds.length || bidStageIds.includes(t.stage.id),
  );
  const isBidPipelineEmpty =
    !isLoadingBids &&
    bidTransitions.length > 0 &&
    bidTransitions.every((t) => (t.stage.bidCount ?? 0) === 0);

  const { totalCount: totalBidsCount, totalValue: totalBidsValue } = computeValueAndCount(
    filteredBidTransitions,
    (t) => t.stage.bidCount ?? 0,
    (t) => t.stage.bidValueSum ?? 0,
  );

  return (
    <Flex column gap={16}>
      <WidgetTitle>Pipeline</WidgetTitle>
      <PipelineBox>
        <Flex column gap={16} width="100%">
          <TabHeader>
            <Tabs
              value={selectedTab}
              onChange={(key) => setSelectedTab(key as PipelineTab)}
              items={TAB_ITEMS}
              padding="0 24px"
              hasBottomBorder={false}
            />
          </TabHeader>
          <ActionsBar>
            {selectedTab === "leads" && !isLoadingLeads && totalLeadsCount > 0 && (
              <Flex flexDirection="column" gap="4" alignItems="flex-start">
                <TotalValueInfo
                  totalValue={totalLeadsValue}
                  totalCount={totalLeadsCount}
                  entityType={EntityType.leads}
                  filters={filters}
                />
              </Flex>
            )}
            {selectedTab === "bids" && !isLoadingBids && totalBidsCount > 0 && (
              <Flex flexDirection="column" gap="4" alignItems="flex-start">
                <TotalValueInfo
                  totalValue={totalBidsValue}
                  totalCount={totalBidsCount}
                  entityType={EntityType.bids}
                  filters={filters}
                />
              </Flex>
            )}
            {/* Grouped so the cursor can move across the filter icons without re-waiting the delay */}
            <TooltipGroup>
              <Filters>
                {selectedTab === "leads" && (
                  <TooltipComponent content="Filter stage">
                    <span>
                      <StageDropdownFilter
                        selectedStages={leadStageIds}
                        onChange={(stageIds) => setLeadFilters({ ...leadFilters, stageIds })}
                        options={processTransitions(leadWorkflow?.transitions ?? [])}
                        loading={isFetchingLeads}
                      />
                    </span>
                  </TooltipComponent>
                )}

                {selectedTab === "bids" && (
                  <TooltipComponent content="Filter stage">
                    <span>
                      <StageDropdownFilter
                        selectedStages={bidStageIds}
                        onChange={(stageIds) => setBidFilters({ ...bidFilters, stageIds })}
                        options={processTransitions(bidWorkflow?.transitions ?? [])}
                        loading={isFetchingBids}
                      />
                    </span>
                  </TooltipComponent>
                )}
                <TooltipComponent content="Filter assignee">
                  <span>
                    <UserDropdownFilter
                      value={
                        filters?.assignee ? filters.assignee : { ids: [], includeNulls: false }
                      }
                      onChange={(value) => setFilters({ ...filters, assignee: value })}
                      options={{ noun: "assignee" }}
                      minified
                    />
                  </span>
                </TooltipComponent>
                <TooltipComponent content="Filter team">
                  <span>
                    <TeamDropdownFilter
                      teamIds={filters.teamIds?.length ? filters.teamIds : []}
                      onChange={(value) => setFilters({ ...filters, teamIds: value })}
                      minified
                    />
                  </span>
                </TooltipComponent>
              </Filters>
            </TooltipGroup>
          </ActionsBar>
          <BarsSection>
            {selectedTab === "leads" ? (
              <LeadsPipelineContent
                isLoading={isLoadingLeads}
                filters={debouncedFilters}
                isPipelineEmpty={isLeadPipelineEmpty}
                filteredResultsEmpty={totalLeadsCount === 0}
                transitions={filteredLeadTransitions}
              />
            ) : (
              <BidsPipelineContent
                isLoading={isLoadingBids}
                filters={debouncedFilters}
                isPipelineEmpty={isBidPipelineEmpty}
                filteredResultsEmpty={totalBidsCount === 0}
                transitions={filteredBidTransitions}
              />
            )}
          </BarsSection>
        </Flex>
      </PipelineBox>
    </Flex>
  );
}

type LeadsPipelineContentProps = {
  transitions: LeadTransition[];
  isLoading: boolean;
  filters: PipelineFilters;
  isPipelineEmpty?: boolean;
  filteredResultsEmpty?: boolean;
};

function TotalValueInfo({
  totalValue,
  totalCount,
  entityType,
  filters,
}: {
  totalValue: number;
  totalCount: number;
  entityType: (typeof EntityType)[keyof typeof EntityType];
  filters: PipelineFilters;
}) {
  const urlParams = {
    stageIds: filters.stageIds ?? [],
    assignee: {
      ids: filters.assignee?.ids ?? [],
      includeNulls: filters.assignee?.includeNulls ?? false,
    },
    teamIds: filters.teamIds ?? [],
  };
  const url =
    entityType === EntityType.leads
      ? generateLeadsSearchUrl(urlParams)
      : generateBidsSearchUrl(urlParams);

  const content = (
    <>
      <Text bold h3>
        {currencyFormatLong(totalValue, "GBP")}
      </Text>
      <Text description bold color="sysTextSecondary">
        {`Value of ${pluralise(totalCount, entityType, undefined)}`}
      </Text>
    </>
  );

  return <StyledRouterLink to={url}>{content}</StyledRouterLink>;
}

function computeValueAndCount<T>(
  transitions: T[],
  getCount: (t: T) => number,
  getValue: (t: T) => number,
) {
  const totalCount = transitions.reduce((sum, t) => sum + getCount(t), 0);
  const totalValue = transitions.reduce((sum, t) => sum + getValue(t), 0);
  return { totalValue, totalCount };
}

function LeadsPipelineContent({
  isLoading,
  filters,
  isPipelineEmpty,
  filteredResultsEmpty,
  transitions,
}: LeadsPipelineContentProps) {
  if (!isLoading) {
    if (isPipelineEmpty) {
      return (
        <EmptyStateContainer>
          <LeadPipelineEmptyState
            eventData={{ "Context source": "Homepage pipeline" }}
            description="Create your first Lead from an expiring contract"
            actionButtonTitle="Browse expiring notices"
            viewName={StandardViewName.Expiries}
          />
        </EmptyStateContainer>
      );
    }

    if (filteredResultsEmpty) {
      return (
        <EmptyStateContainer>
          <EmptyState
            type="noResults"
            heading="No results"
            description="Try another filter to view your Leads"
            width="100%"
            size="large"
          />
        </EmptyStateContainer>
      );
    }
  }

  const data: StageItem[] =
    transitions.length > 0
      ? transitions
          .map((transition) => ({
            stageId: transition.stage.id,
            order: transition.order,
            count: transition.stage.leadCount ?? 0,
            text: transition.stage.name,
            group: transition.group,
            color: transition.stage.color,
            value: transition.stage.leadValueSum ?? 0,
            iconName: transition.stage.iconName,
            iconColor: transition.stage.iconColor,
          }))
          .sort((a, b) => a.order - b.order)
      : createLeadLoadingPlaceholders();

  const highestCount = Math.max(...data.map((d) => d.count), 0);

  return (
    <Flex width="100%" gap={16} alignItems="flex-end">
      {data.map((item) => (
        <PipelineStage
          key={item.stageId}
          item={item}
          highestCount={highestCount}
          value={item.value}
          isLoading={isLoading}
          linkUrl={generateLeadsSearchUrl({
            stageIds: [item.stageId],
            assignee: filters.assignee,
            teamIds: filters.teamIds,
          })}
          trackingEventName={EventNames.pipelineViewed}
          pipelineTab="leads"
        />
      ))}
    </Flex>
  );
}

type BidsPipelineContentProps = {
  isLoading: boolean;
  filters: PipelineFilters;
  isPipelineEmpty?: boolean;
  filteredResultsEmpty?: boolean;
  transitions: BidTransition[];
};

function BidsPipelineContent({
  isLoading,
  filters,
  isPipelineEmpty,
  transitions,
  filteredResultsEmpty,
}: BidsPipelineContentProps) {
  if (!isLoading) {
    if (isPipelineEmpty) {
      return (
        <EmptyStateContainer>
          <BidPipelineEmptyState eventData={{ "Context source": "Homepage pipeline" }} />
        </EmptyStateContainer>
      );
    }

    if (filteredResultsEmpty) {
      return (
        <EmptyStateContainer>
          <EmptyState
            type="noResults"
            heading="No results"
            description="Try another filter to view your Bids"
            width="100%"
            size="large"
          />
        </EmptyStateContainer>
      );
    }
  }

  const data: StageItem[] =
    transitions.length > 0
      ? transitions
          .map((transition) => ({
            stageId: transition.stage.id,
            order: transition.order,
            count: transition.stage.bidCount ?? 0,
            text: transition.stage.name,
            group: transition.group,
            color: transition.stage.color,
            iconName: transition.stage.iconName,
            iconColor: transition.stage.iconColor,
            value: transition.stage.bidValueSum ?? 0,
          }))
          .sort((a, b) => a.order - b.order)
      : createBidLoadingPlaceholders();

  const highestCount = Math.max(...data.map((d) => d.count), 0);

  return (
    <Flex width="100%" gap={16} alignItems="flex-end">
      {data.map((item) => (
        <PipelineStage
          key={item.stageId}
          item={item}
          highestCount={highestCount}
          isLoading={isLoading}
          linkUrl={generateBidsSearchUrl({
            stageIds: [item.stageId],
            assignee: filters.assignee,
            teamIds: filters.teamIds,
          })}
          trackingEventName={EventNames.pipelineViewed}
          pipelineTab="bids"
          value={item.value}
        />
      ))}
    </Flex>
  );
}

// Loading placeholder values create an descending chart shape
const LEAD_LOADING_VALUES = [85, 65, 45, 30, 20, 12, 5];
const BID_LOADING_VALUES = [70, 50, 35, 20, 10, 4];

function createLeadLoadingPlaceholders(): StageItem[] {
  return LEAD_LOADING_VALUES.map((value, i) => ({
    stageId: `loading-${i}`,
    order: i,
    count: value,
    text: "",
    group: "",
    color: "",
    iconName: "",
    iconColor: "",
    value: 0,
  }));
}

function createBidLoadingPlaceholders(): StageItem[] {
  return BID_LOADING_VALUES.map((value, i) => ({
    stageId: `loading-${i}`,
    order: i,
    count: value,
    text: "",
    group: "",
    color: "",
    iconName: "",
    iconColor: "",
    value: 0,
  }));
}

const PipelineBox = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: "4px",
  backgroundColor: theme.colors.sysBackgroundDefault,
  padding: 0,
  paddingBottom: "24px",
}));

const TabHeader = styled.div(({ theme }) => ({
  borderBottom: `1px solid ${theme.colors.sysBorderDecorative}`,
  "& [role='tablist']": {
    height: "40px",
  },
}));

const ActionsBar = styled(Flex)({
  gap: 8,
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 24px",
  minHeight: "36px",
});

const Filters = styled(Flex)({
  gap: 8,
  alignItems: "center",
  marginLeft: "auto",
});

const BarsSection = styled.div({
  padding: "0 24px",
});

const EmptyStateContainer = styled(Flex)({
  justifyContent: "center",
  alignItems: "center",
  minHeight: "200px",
});

const StyledRouterLink = styled(Link)(({ theme }) => ({
  textDecoration: "none",
  "&:hover": {
    textDecoration: "none",
    color: theme.colors.sysInfoHover,
    "& *": { color: "inherit" },
  },
}));

export default TabbedPipeline;
